/**
 * Tickets.tsx — Shield Support Ticket System
 *
 * Displays all tickets for a customer with AI-generated diagnosis,
 * recommended actions, SLA tracking, activity timeline, and comments.
 * All content is in plain English — no technical jargon.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import API_BASE from '../config/api';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import CloseIcon from '@mui/icons-material/Close';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import PersonIcon from '@mui/icons-material/Person';
import RefreshIcon from '@mui/icons-material/Refresh';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import { useAuth } from '../context/AuthContext';

// ── Types ─────────────────────────────────────────────────────────────────────

interface Customer { id: string; name: string; organisation: string; }

interface TicketComment {
  id: string; ticket_id: string; author: string; content: string; created_at: string;
}
interface TicketActivityEntry {
  id: string; ticket_id: string; action: string;
  old_value: string; new_value: string; performed_by: string; created_at: string;
}

interface Ticket {
  id: string;
  ticket_number: string;
  customer_id: string;
  finding_id: string | null;
  fix_request_id: string | null;
  scan_id: string | null;
  check_id: string;
  is_recurring: boolean;
  category: string;
  priority: string;
  provider: string;
  os_type: string;
  title: string;
  description: string;
  customer_note: string;
  ai_diagnosis: string;
  ai_recommended_action: string;
  estimated_fix_time: string;
  customer_name: string;
  customer_email: string;
  company_name: string;
  cloud_provider: string;
  affected_resource: string;
  severity_score: number;
  status: string;
  assigned_to: string;
  created_at: string;
  updated_at: string;
  resolved_at: string | null;
  sla_deadline: string | null;
  sla_breached: boolean;
  comments?: TicketComment[];
  activity?: TicketActivityEntry[];
}

interface Stats {
  open_count: number;
  in_progress_count: number;
  critical_count: number;
  resolved_this_week: number;
  sla_breached_count: number;
  total: number;
}

interface ScanGroup {
  scan_id: string | null;
  scan_date: string | null;
  scan_number: number;
  provider: string;
  total_tickets: number;
  critical_count: number;
  recurring_count: number;
  tickets: Ticket[];
}

// ── Colour maps ───────────────────────────────────────────────────────────────

type ChipColor = 'error' | 'warning' | 'info' | 'success' | 'default';

const PRIORITY_COLOR: Record<string, ChipColor> = {
  critical: 'error', high: 'error', medium: 'warning', low: 'success',
};

const STATUS_COLOR: Record<string, ChipColor> = {
  open: 'warning', in_progress: 'info', waiting_customer: 'default',
  resolved: 'success', closed: 'default',
};

const STATUS_LABEL: Record<string, string> = {
  open: 'Open', in_progress: 'In Progress', waiting_customer: 'Waiting',
  resolved: 'Resolved', closed: 'Closed',
};

const PROVIDER_LABELS: Record<string, string> = {
  aws: 'AWS', gcp: 'GCP', azure: 'Azure', ssh: 'Private Server',
  all: 'All', shadow_ai: 'Shadow AI',
};

const PROVIDER_COLORS: Record<string, string> = {
  aws: '#f77f00', gcp: '#2dc653', azure: '#00b4d8', ssh: '#9c6ade',
  shadow_ai: '#ef233c', all: '#666',
};

// ── Helper components ─────────────────────────────────────────────────────────

function PriorityChip({ priority }: { priority: string }) {
  return (
    <Chip
      label={priority.toUpperCase()}
      color={PRIORITY_COLOR[priority] ?? 'default'}
      size="small"
      sx={{ fontWeight: 700, fontSize: '0.65rem' }}
    />
  );
}

function StatusChip({ status }: { status: string }) {
  return (
    <Chip
      label={STATUS_LABEL[status] ?? status}
      color={STATUS_COLOR[status] ?? 'default'}
      size="small"
      variant="outlined"
      sx={{ fontWeight: 600 }}
    />
  );
}

function ProviderBadge({ provider }: { provider: string }) {
  return (
    <Chip
      label={PROVIDER_LABELS[provider] ?? provider.toUpperCase()}
      size="small"
      sx={{
        fontWeight: 700, fontSize: '0.65rem',
        bgcolor: PROVIDER_COLORS[provider] ?? '#555', color: '#000',
      }}
    />
  );
}

function SlaCell({ deadline, breached }: { deadline: string | null; breached: boolean }) {
  if (!deadline) return <Typography variant="caption" color="text.disabled">—</Typography>;
  const label = new Date(deadline).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
  return (
    <Tooltip title={breached ? 'SLA deadline has passed!' : 'SLA deadline'}>
      <Box display="flex" alignItems="center" gap={0.5}>
        {breached && <WarningAmberIcon sx={{ fontSize: 14, color: 'error.main' }} />}
        <Typography
          variant="caption"
          color={breached ? 'error.main' : 'text.secondary'}
          fontWeight={breached ? 700 : 400}
        >
          {label}
        </Typography>
      </Box>
    </Tooltip>
  );
}

function formatTime(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

// ── Ticket Detail Dialog ──────────────────────────────────────────────────────

function TicketDetailDialog({
  ticket,
  onClose,
  onStatusUpdate,
}: {
  ticket: Ticket | null;
  onClose: () => void;
  onStatusUpdate: (ticketId: string, status: string, comment: string, engineer: string) => Promise<void>;
}) {
  const [newStatus,   setNewStatus]   = useState('');
  const [comment,     setComment]     = useState('');
  const [engineer,    setEngineer]    = useState('');
  const [updating,    setUpdating]    = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);

  useEffect(() => {
    if (ticket) {
      setNewStatus(ticket.status);
      setUpdateError(null);
    }
  }, [ticket?.id]);

  if (!ticket) return null;

  const handleUpdate = async () => {
    setUpdating(true);
    setUpdateError(null);
    try {
      await onStatusUpdate(ticket.id, newStatus, comment, engineer);
      setComment('');
    } catch (err: any) {
      setUpdateError(err.response?.data?.detail ?? err.message ?? 'Update failed');
    } finally {
      setUpdating(false);
    }
  };

  const authorIcon = (author: string) => {
    if (author === 'ai') return <SmartToyIcon sx={{ fontSize: 14 }} />;
    if (author === 'customer') return <PersonIcon sx={{ fontSize: 14 }} />;
    return <AssignmentTurnedInIcon sx={{ fontSize: 14 }} />;
  };

  return (
    <Dialog open={!!ticket} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', pb: 1 }}>
        <Box>
          <Typography variant="h6" fontWeight={700}>{ticket.ticket_number}</Typography>
          <Stack direction="row" spacing={1} mt={0.5} flexWrap="wrap">
            <PriorityChip priority={ticket.priority} />
            <StatusChip status={ticket.status} />
            <ProviderBadge provider={ticket.cloud_provider || ticket.provider} />
          </Stack>
        </Box>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* Title */}
        <Typography variant="h6" gutterBottom>{ticket.title}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>{ticket.description}</Typography>

        <Grid container spacing={3}>
          {/* Left column */}
          <Grid item xs={12} md={7}>

            {/* What's wrong */}
            <Paper elevation={0} sx={{ border: 1, borderColor: 'divider', p: 2, mb: 2 }}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <ErrorOutlineIcon sx={{ color: 'error.main', fontSize: 18 }} />
                <Typography variant="subtitle2" fontWeight={700}>What's wrong</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {ticket.ai_diagnosis || 'AI analysis pending.'}
              </Typography>
            </Paper>

            {/* What we'll do */}
            <Paper elevation={0} sx={{ border: 1, borderColor: 'divider', p: 2, mb: 2 }}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <LightbulbIcon sx={{ color: 'warning.main', fontSize: 18 }} />
                <Typography variant="subtitle2" fontWeight={700}>What we'll do</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {ticket.ai_recommended_action || 'Our engineer will assess and act on this.'}
              </Typography>
            </Paper>

            {/* Customer note */}
            {ticket.customer_note && (
              <Paper elevation={0} sx={{ border: 1, borderColor: 'primary.dark', p: 2, mb: 2 }}>
                <Typography variant="subtitle2" fontWeight={700} mb={0.5}>Your note</Typography>
                <Typography variant="body2" color="text.secondary">{ticket.customer_note}</Typography>
              </Paper>
            )}

            {/* Comments */}
            {(ticket.comments?.length ?? 0) > 0 && (
              <Box mb={2}>
                <Typography variant="subtitle2" fontWeight={700} mb={1}>Updates</Typography>
                <Stack spacing={1}>
                  {ticket.comments!.map((c) => (
                    <Paper
                      key={c.id}
                      elevation={0}
                      sx={{
                        border: 1,
                        borderColor: c.author === 'ai' ? 'primary.dark' : 'divider',
                        p: 1.5,
                        bgcolor: c.author === 'ai' ? 'rgba(0,180,216,0.05)' : 'transparent',
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                        {authorIcon(c.author)}
                        <Typography
                          variant="caption"
                          fontWeight={600}
                          color={c.author === 'ai' ? 'primary' : 'text.secondary'}
                        >
                          {c.author === 'ai' ? 'Shield AI' : c.author === 'customer' ? 'Partner' : c.author}
                        </Typography>
                        <Typography variant="caption" color="text.disabled" ml="auto">
                          {formatTime(c.created_at)}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">{c.content}</Typography>
                    </Paper>
                  ))}
                </Stack>
              </Box>
            )}

            {/* Status update */}
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" fontWeight={700} mb={1.5}>Update Status</Typography>
            {updateError && <Alert severity="error" sx={{ mb: 1 }}>{updateError}</Alert>}
            <Stack spacing={1.5}>
              <FormControl size="small" fullWidth>
                <InputLabel>New Status</InputLabel>
                <Select value={newStatus} label="New Status" onChange={(e) => setNewStatus(e.target.value)}>
                  <MenuItem value="open">Open</MenuItem>
                  <MenuItem value="in_progress">In Progress</MenuItem>
                  <MenuItem value="waiting_customer">Waiting on Partner</MenuItem>
                  <MenuItem value="resolved">Resolved</MenuItem>
                  <MenuItem value="closed">Closed</MenuItem>
                </Select>
              </FormControl>
              <TextField
                size="small" fullWidth label="Engineer name (optional)"
                value={engineer} onChange={(e) => setEngineer(e.target.value)}
              />
              <TextField
                size="small" fullWidth multiline rows={2}
                label="Comment (optional)"
                placeholder="Add a note about what was done..."
                value={comment} onChange={(e) => setComment(e.target.value)}
              />
              <Button
                variant="contained" size="small" onClick={handleUpdate}
                disabled={updating || newStatus === ticket.status}
                startIcon={updating ? <CircularProgress size={14} color="inherit" /> : <AssignmentTurnedInIcon />}
              >
                {updating ? 'Updating…' : 'Update Ticket'}
              </Button>
            </Stack>
          </Grid>

          {/* Right column — metadata + activity */}
          <Grid item xs={12} md={5}>
            <Paper elevation={0} sx={{ border: 1, borderColor: 'divider', p: 2, mb: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} mb={1.5}>Details</Typography>
              <Stack spacing={1}>
                {[
                  ['Estimated fix', ticket.estimated_fix_time || '—'],
                  ['Affected resource', ticket.affected_resource || '—'],
                  ['OS', ticket.os_type.replace('_', ' ')],
                  ['Partner', ticket.customer_name],
                  ['Company', ticket.company_name],
                  ['Assigned to', ticket.assigned_to || 'Unassigned'],
                  ['Created', formatTime(ticket.created_at)],
                  ['SLA deadline', ticket.sla_deadline ? formatTime(ticket.sla_deadline) : '—'],
                ].map(([label, value]) => (
                  <Box key={label} display="flex" justifyContent="space-between" gap={1}>
                    <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                      {label}
                    </Typography>
                    <Typography
                      variant="caption"
                      textAlign="right"
                      sx={{ wordBreak: 'break-all' }}
                      color={label === 'SLA deadline' && ticket.sla_breached ? 'error.main' : 'text.primary'}
                      fontWeight={label === 'SLA deadline' && ticket.sla_breached ? 700 : 400}
                    >
                      {value}
                      {label === 'SLA deadline' && ticket.sla_breached && ' ⚠ BREACHED'}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Paper>

            {/* Activity timeline */}
            {(ticket.activity?.length ?? 0) > 0 && (
              <Box>
                <Typography variant="subtitle2" fontWeight={700} mb={1}>Activity</Typography>
                <Stack spacing={0}>
                  {ticket.activity!.map((a, idx) => (
                    <Box key={a.id} display="flex" gap={1.5}>
                      {/* Dot + connector */}
                      <Box display="flex" flexDirection="column" alignItems="center" sx={{ width: 16, flexShrink: 0 }}>
                        <Box
                          sx={{
                            width: 10, height: 10, borderRadius: '50%', mt: 0.8, flexShrink: 0,
                            bgcolor: a.action === 'created' ? 'primary.main'
                              : a.new_value === 'resolved' ? 'success.main'
                              : 'grey.600',
                          }}
                        />
                        {idx < ticket.activity!.length - 1 && (
                          <Box sx={{ width: 2, flex: 1, bgcolor: 'divider', my: 0.25 }} />
                        )}
                      </Box>
                      {/* Content */}
                      <Box pb={idx < ticket.activity!.length - 1 ? 1 : 0}>
                        <Typography variant="caption" fontWeight={600} textTransform="capitalize">
                          {a.action.replace('_', ' ')}
                          {a.old_value && a.new_value ? `: ${a.old_value} → ${a.new_value}` : ''}
                        </Typography>
                        <Typography variant="caption" color="text.disabled" display="block">
                          {a.performed_by} · {formatTime(a.created_at)}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
}

// ── KPI card ──────────────────────────────────────────────────────────────────

function StatCard({
  label, value, accent, icon, onClick,
}: {
  label: string; value: number | string; accent?: string; icon?: React.ReactNode; onClick?: () => void;
}) {
  return (
    <Card
      elevation={0}
      onClick={onClick}
      sx={{
        border: 1, borderColor: 'divider', height: '100%',
        ...(onClick && {
          cursor: 'pointer',
          '&:hover': { borderColor: 'primary.main', transform: 'translateY(-2px)', transition: 'all 0.2s' },
        }),
      }}
    >
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {icon && <Box sx={{ color: accent ?? 'text.secondary', opacity: 0.8 }}>{icon}</Box>}
        <Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>{label}</Typography>
          <Typography variant="h4" fontWeight={700} color={accent ?? 'text.primary'}>{value}</Typography>
        </Box>
      </CardContent>
    </Card>
  );
}

// ── Scan Group Card ──────────────────────────────────────────────────────────

function ScanGroupCard({
  group, expanded, onToggle, onViewTicket, detailLoading,
}: {
  group: ScanGroup;
  expanded: boolean;
  onToggle: () => void;
  onViewTicket: (ticketId: string) => void;
  detailLoading: boolean;
}) {
  const scanLabel = group.scan_id
    ? `${PROVIDER_LABELS[group.provider] ?? group.provider.toUpperCase()} Scan`
    : 'Other Tickets';

  const dateLabel = group.scan_date
    ? new Date(group.scan_date).toLocaleString(undefined, {
        day: 'numeric', month: 'long', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      })
    : '';

  return (
    <Paper elevation={0} sx={{ border: 1, borderColor: 'divider', mb: 2, overflow: 'hidden' }}>
      <Box
        onClick={onToggle}
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: 2, py: 1.5, cursor: 'pointer',
          bgcolor: expanded ? 'action.hover' : 'transparent',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <Box>
          <Stack direction="row" spacing={1} alignItems="center" mb={0.5} flexWrap="wrap">
            <Typography variant="subtitle1" fontWeight={700}>{scanLabel}</Typography>
            {dateLabel && (
              <Typography variant="body2" color="text.secondary">&mdash; {dateLabel}</Typography>
            )}
            {group.scan_id && (
              <Typography variant="caption" color="text.disabled" sx={{ fontFamily: 'monospace' }}>
                {group.scan_id.slice(0, 8)}
              </Typography>
            )}
          </Stack>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {group.scan_number > 0 && (
              <Chip label={`Scan #${group.scan_number}`} size="small"
                sx={{ fontWeight: 600, fontSize: '0.65rem' }} />
            )}
            {group.critical_count > 0 && (
              <Chip label={`${group.critical_count} Critical`} color="error" size="small"
                sx={{ fontWeight: 600, fontSize: '0.65rem' }} />
            )}
            {group.recurring_count > 0 && (
              <Chip label={`${group.recurring_count} Recurring`} color="warning" size="small"
                variant="outlined" sx={{ fontWeight: 600, fontSize: '0.65rem' }} />
            )}
            <Chip label={`${group.total_tickets} total`} size="small"
              sx={{ fontSize: '0.65rem' }} />
          </Stack>
        </Box>
        <ExpandMoreIcon sx={{
          transform: expanded ? 'rotate(180deg)' : 'none',
          transition: '0.2s', color: 'text.secondary', flexShrink: 0,
        }} />
      </Box>

      <Collapse in={expanded}>
        <Divider />
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'background.default' } }}>
              <TableCell>Ticket #</TableCell>
              <TableCell>Priority</TableCell>
              <TableCell sx={{ minWidth: 240 }}>Issue</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>SLA</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {group.tickets.map((t) => (
              <TableRow
                key={t.id}
                hover
                sx={{
                  borderLeft: 4,
                  borderLeftColor:
                    t.sla_breached ? 'error.main'
                    : t.priority === 'critical' ? 'error.dark'
                    : t.priority === 'high' ? 'warning.dark'
                    : 'transparent',
                }}
              >
                <TableCell>
                  <Typography variant="body2" fontWeight={700} color="primary">
                    {t.ticket_number}
                  </Typography>
                </TableCell>
                <TableCell><PriorityChip priority={t.priority} /></TableCell>
                <TableCell>
                  <Box>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Typography variant="body2" fontWeight={500} noWrap sx={{ maxWidth: 240 }}>
                        {t.title}
                      </Typography>
                      {t.is_recurring && (
                        <Chip label="Recurring ↺" size="small" variant="outlined" color="warning"
                          sx={{ fontWeight: 600, fontSize: '0.6rem', height: 20 }} />
                      )}
                    </Box>
                    {t.affected_resource && (
                      <Typography variant="caption" color="text.disabled" noWrap display="block">
                        {t.affected_resource}
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell><StatusChip status={t.status} /></TableCell>
                <TableCell>
                  <SlaCell deadline={t.sla_deadline} breached={t.sla_breached} />
                </TableCell>
                <TableCell align="right">
                  <Button size="small" variant="outlined"
                    onClick={() => onViewTicket(t.id)} disabled={detailLoading}>
                    View
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Collapse>
    </Paper>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function Tickets() {
  const { user, loading: authLoading } = useAuth();
  const isCustomer = !authLoading && user?.role === 'customer';

  const [customers,        setCustomers]        = useState<Customer[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<string>('');
  const [scanGroups,       setScanGroups]       = useState<ScanGroup[]>([]);
  const [stats,            setStats]            = useState<Stats | null>(null);
  const [loading,          setLoading]          = useState(false);
  const [error,            setError]            = useState<string | null>(null);

  const [scanFilter,       setScanFilter]       = useState<string>('all');
  const [statusFilter,     setStatusFilter]     = useState<string>('all');
  const [priorityFilter,   setPriorityFilter]   = useState<string>('all');

  const [expandedScans,    setExpandedScans]    = useState<Set<string>>(new Set());
  const initialExpandDone  = useRef(false);

  const [detailTicket,     setDetailTicket]     = useState<Ticket | null>(null);
  const [detailLoading,    setDetailLoading]    = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (isCustomer) {
      setSelectedCustomer(user?.customer_id ?? '');
      return;
    }
    axios.get<Customer[]>(`${API_BASE}/v1/customers/`)
      .then((r) => {
        setCustomers(r.data);
        if (r.data.length > 0) setSelectedCustomer(r.data[0].id);
      })
      .catch(() => setError('Could not load customers'));
  }, [authLoading, isCustomer, user?.customer_id]);

  const load = useCallback(async () => {
    if (!selectedCustomer) return;
    setLoading(true);
    setError(null);
    try {
      const [groupsRes, statsRes] = await Promise.all([
        axios.get<ScanGroup[]>(`${API_BASE}/v1/tickets/by-scan/${selectedCustomer}`),
        axios.get<Stats>(`${API_BASE}/v1/tickets/customer/${selectedCustomer}/stats`),
      ]);
      setScanGroups(groupsRes.data);
      setStats(statsRes.data);
    } catch (err: any) {
      setError(err.response?.data?.detail ?? err.message ?? 'Failed to load tickets');
    } finally {
      setLoading(false);
    }
  }, [selectedCustomer]);

  useEffect(() => { load(); }, [load]);

  // Client-side filtering
  const filteredGroups = useMemo(() => {
    return scanGroups
      .map(group => {
        let tickets = group.tickets;
        if (statusFilter !== 'all') tickets = tickets.filter(t => t.status === statusFilter);
        if (priorityFilter !== 'all') tickets = tickets.filter(t => t.priority === priorityFilter);
        return {
          ...group,
          tickets,
          total_tickets: tickets.length,
          critical_count: tickets.filter(t => t.priority === 'critical').length,
          recurring_count: tickets.filter(t => t.is_recurring).length,
        };
      })
      .filter(g => g.tickets.length > 0)
      .filter(g => scanFilter === 'all' || (g.scan_id ?? 'other') === scanFilter);
  }, [scanGroups, scanFilter, statusFilter, priorityFilter]);

  const scanOptions = useMemo(() => {
    return scanGroups
      .filter(g => g.scan_id)
      .map(g => ({
        id: g.scan_id!,
        label: `${PROVIDER_LABELS[g.provider] ?? g.provider.toUpperCase()} — ${
          g.scan_date
            ? new Date(g.scan_date).toLocaleDateString(undefined, {
                month: 'short', day: 'numeric', year: 'numeric',
              })
            : 'Unknown'
        }`,
      }));
  }, [scanGroups]);

  // Auto-expand latest scan on first load
  useEffect(() => {
    if (!initialExpandDone.current && filteredGroups.length > 0) {
      setExpandedScans(new Set([filteredGroups[0].scan_id ?? 'other']));
      initialExpandDone.current = true;
    }
  }, [filteredGroups]);

  const toggleScan = (scanId: string) => {
    setExpandedScans(prev => {
      const next = new Set(prev);
      if (next.has(scanId)) next.delete(scanId); else next.add(scanId);
      return next;
    });
  };

  const openDetail = async (ticketId: string) => {
    setDetailLoading(true);
    try {
      const r = await axios.get<Ticket>(`${API_BASE}/v1/tickets/detail/${ticketId}`);
      setDetailTicket(r.data);
    } catch (err: any) {
      setError(err.response?.data?.detail ?? 'Could not load ticket details');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleStatusUpdate = async (
    ticketId: string, status: string, comment: string, engineer: string,
  ) => {
    await axios.patch(`${API_BASE}/v1/tickets/${ticketId}/status`, { status, comment, engineer });
    const r = await axios.get<Ticket>(`${API_BASE}/v1/tickets/detail/${ticketId}`);
    setDetailTicket(r.data);
    await load();
  };

  const totalTickets = useMemo(
    () => scanGroups.reduce((sum, g) => sum + g.total_tickets, 0),
    [scanGroups],
  );

  return (
    <Box>
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3} flexWrap="wrap" gap={1}>
        <Box>
          <Typography variant="h4">Support Tickets</Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Let our security engineers handle it for you
          </Typography>
        </Box>
        <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
          Refresh
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* ── Filter bar ─────────────────────────────────────────────────────── */}
      <Stack direction="row" spacing={2} mb={3} flexWrap="wrap">
        {!isCustomer && (
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Partner</InputLabel>
            <Select value={selectedCustomer} label="Partner" onChange={(e) => setSelectedCustomer(e.target.value)}>
              {customers.map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Scan</InputLabel>
          <Select value={scanFilter} label="Scan" onChange={(e) => setScanFilter(e.target.value)}>
            <MenuItem value="all">All Scans</MenuItem>
            {scanOptions.map((s) => (
              <MenuItem key={s.id} value={s.id}>{s.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Priority</InputLabel>
          <Select value={priorityFilter} label="Priority" onChange={(e) => setPriorityFilter(e.target.value)}>
            <MenuItem value="all">All Priorities</MenuItem>
            <MenuItem value="critical">Critical</MenuItem>
            <MenuItem value="high">High</MenuItem>
            <MenuItem value="medium">Medium</MenuItem>
            <MenuItem value="low">Low</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
            <MenuItem value="all">All Statuses</MenuItem>
            <MenuItem value="open">Open</MenuItem>
            <MenuItem value="in_progress">In Progress</MenuItem>
            <MenuItem value="waiting_customer">Waiting</MenuItem>
            <MenuItem value="resolved">Resolved</MenuItem>
            <MenuItem value="closed">Closed</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      {/* ── KPI stats bar ──────────────────────────────────────────────────── */}
      <Grid container spacing={2} mb={4}>
        <Grid item xs={6} sm={3}>
          <StatCard label="Open Tickets" value={loading ? '—' : stats?.open_count ?? 0}
            accent="warning.main" icon={<ConfirmationNumberIcon />}
            onClick={() => { setStatusFilter('open'); setPriorityFilter('all'); setScanFilter('all'); }} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard label="In Progress" value={loading ? '—' : stats?.in_progress_count ?? 0}
            accent="#00b4d8" icon={<AccessTimeIcon />}
            onClick={() => { setStatusFilter('in_progress'); setPriorityFilter('all'); setScanFilter('all'); }} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard label="Critical" value={loading ? '—' : stats?.critical_count ?? 0}
            accent="#ef233c" icon={<ErrorOutlineIcon />}
            onClick={() => { setPriorityFilter('critical'); setStatusFilter('all'); setScanFilter('all'); }} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard label="Resolved This Week" value={loading ? '—' : stats?.resolved_this_week ?? 0}
            accent="#2dc653" icon={<AssignmentTurnedInIcon />}
            onClick={() => { setStatusFilter('resolved'); setPriorityFilter('all'); setScanFilter('all'); }} />
        </Grid>
      </Grid>

      {/* SLA warning banner */}
      {(stats?.sla_breached_count ?? 0) > 0 && (
        <Alert severity="error" sx={{ mb: 3 }} icon={<WarningAmberIcon />}>
          <strong>{stats!.sla_breached_count} ticket{stats!.sla_breached_count !== 1 ? 's have' : ' has'} breached SLA</strong>
          {' '}— these require immediate attention.
        </Alert>
      )}

      {/* ── Scan groups ────────────────────────────────────────────────────── */}
      {loading ? (
        <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
      ) : filteredGroups.length === 0 ? (
        <Alert severity="info" icon={<ConfirmationNumberIcon />}>
          {totalTickets === 0
            ? <>No tickets found. Click <strong>&quot;Let Shield Fix It For Me&quot;</strong> on any finding or Shadow AI event to create one.</>
            : 'No tickets match the current filters.'}
        </Alert>
      ) : (
        <Box>
          {filteredGroups.map((group) => {
            const key = group.scan_id ?? 'other';
            return (
              <ScanGroupCard
                key={key}
                group={group}
                expanded={expandedScans.has(key)}
                onToggle={() => toggleScan(key)}
                onViewTicket={openDetail}
                detailLoading={detailLoading}
              />
            );
          })}
        </Box>
      )}

      {/* ── Ticket detail dialog ───────────────────────────────────────────── */}
      <TicketDetailDialog
        ticket={detailTicket}
        onClose={() => setDetailTicket(null)}
        onStatusUpdate={handleStatusUpdate}
      />
    </Box>
  );
}
